import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  Tag,
  XCircle,
  Pencil,
  Trash2,
  Copy,
  Plus,
  List,
  ToggleLeft,
  ArrowUpCircle,
  ArrowDownCircle,
  FlaskConical,
  Zap,
} from 'lucide-react';
import { fetchTestTags, createTestTags, updateTestTag, deleteTestTag, toggleTestTag, promoteTestTag, demoteTestTag, previewTestTags } from '@/api/admin';
import type { TestTag } from '@/types/admin';
import { toast } from 'sonner';

const VERSIONS = ['16.0', '17.0', '18.0', '19.0'];
const COMMON_TAGS = ['post_install', 'at_install', 'standard'];

interface EditingTag {
  id: number;
  tag: string;
  description: string;
  odoo_version: string;
  active: boolean;
}

export function AdminTestTagsPage() {
  const queryClient = useQueryClient();

  // Filters
  const [page, setPage] = useState(1);
  const [odooVersion, setOdooVersion] = useState('');
  const [active, setActive] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');  // 'active', 'staged', or '' for all
  const [search, setSearch] = useState('');

  // Create form
  const [bulkMode, setBulkMode] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [bulkTags, setBulkTags] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVersions, setNewVersions] = useState<string[]>(VERSIONS);
  const [newActive, setNewActive] = useState(true);

  // Edit dialog
  const [editingTag, setEditingTag] = useState<EditingTag | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Preview
  const [previewVersion, setPreviewVersion] = useState('17.0');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-test-tags', page, odooVersion, active, statusFilter, search],
    queryFn: () =>
      fetchTestTags({
        page,
        odoo_version: odooVersion || undefined,
        active: active || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      }),
  });

  const { data: preview } = useQuery({
    queryKey: ['admin-test-tags-preview', previewVersion],
    queryFn: () => previewTestTags(previewVersion),
  });

  const createMutation = useMutation({
    mutationFn: () => {
      if (bulkMode) {
        return createTestTags({
          tags_bulk: bulkTags,
          description: newDescription,
          odoo_versions: newVersions,
          active: newActive,
        });
      }
      return createTestTags({
        tag: newTag,
        description: newDescription,
        odoo_versions: newVersions,
        active: newActive,
      });
    },
    onSuccess: () => {
      toast.success('Tag(s) created successfully');
      setNewTag('');
      setBulkTags('');
      setNewDescription('');
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create tag(s)'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: EditingTag) =>
      updateTestTag(payload.id, {
        tag: payload.tag,
        description: payload.description,
        active: payload.active,
        odoo_versions: [payload.odoo_version],
      }),
    onSuccess: () => {
      toast.success('Tag updated');
      setEditDialogOpen(false);
      setEditingTag(null);
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => toggleTestTag(id),
    onSuccess: () => {
      toast.success('Tag toggled');
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to toggle'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTestTag(id),
    onSuccess: () => {
      toast.success('Tag deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete'),
  });

  const promoteMutation = useMutation({
    mutationFn: (id: number) => promoteTestTag(id),
    onSuccess: (data) => {
      toast.success(data.message || 'Tag promoted to active');
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to promote'),
  });

  const demoteMutation = useMutation({
    mutationFn: (id: number) => demoteTestTag(id),
    onSuccess: (data) => {
      toast.success(data.message || 'Tag demoted to staged');
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags'] });
      queryClient.invalidateQueries({ queryKey: ['admin-test-tags-preview'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to demote'),
  });

  const tags = data?.tags || [];
  const pagination = data?.pagination;
  const stats = data?.stats || { total: 0, active: 0, inactive: 0 };
  const versionCounts = data?.version_counts || {};

  const openEditDialog = (tag: TestTag) => {
    setEditingTag({
      id: tag.id,
      tag: tag.tag,
      description: tag.description || '',
      odoo_version: tag.odoo_version,
      active: tag.active,
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (tag: TestTag) => {
    if (confirm(`Delete tag "${tag.tag}" for Odoo ${tag.odoo_version}?`)) {
      deleteMutation.mutate(tag.id);
    }
  };

  const handleVersionToggle = (version: string) => {
    if (newVersions.includes(version)) {
      if (newVersions.length > 1) {
        setNewVersions(newVersions.filter((v) => v !== version));
      }
    } else {
      setNewVersions([...newVersions, version]);
    }
  };

  const fillCommonTags = () => {
    setBulkTags(COMMON_TAGS.join('\n'));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const canCreate = bulkMode ? bulkTags.trim().length > 0 : newTag.trim().length > 0;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Test Tags</h1>
          <p className="text-sm text-muted-foreground">Manage Odoo test exclusion tags per version</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Tag className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Tags</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active (All Builds)</p>
                <p className="text-2xl font-semibold">{stats.status_active || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FlaskConical className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Staged (CI Only)</p>
                <p className="text-2xl font-semibold">{stats.status_staged || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <XCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Disabled</p>
                <p className="text-2xl font-semibold">{stats.inactive}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">By Version</p>
              <div className="flex flex-wrap gap-1">
                {VERSIONS.map((v) => (
                  <Badge key={v} variant="outline" className="text-xs">
                    {v}: {versionCounts[v] || 0}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Tag Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Create Tag{bulkMode ? 's (Bulk)' : ''}</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setBulkMode(!bulkMode)}>
              {bulkMode ? <Tag className="h-4 w-4 mr-2" /> : <List className="h-4 w-4 mr-2" />}
              {bulkMode ? 'Single Mode' : 'Bulk Mode'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bulkMode ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Tags (one per line)</Label>
                  <Button variant="ghost" size="sm" onClick={fillCommonTags}>
                    Fill Common Tags
                  </Button>
                </div>
                <Textarea
                  placeholder="post_install&#10;at_install&#10;standard"
                  value={bulkTags}
                  onChange={(e) => setBulkTags(e.target.value)}
                  rows={4}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Tag Name</Label>
                <Input
                  placeholder="e.g., post_install"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                placeholder="Why this tag is excluded"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Odoo Versions</Label>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewVersions(VERSIONS)}
                >
                  Select All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewVersions([VERSIONS[0]])}
                >
                  Select None
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {VERSIONS.map((v) => (
                <Button
                  key={v}
                  variant={newVersions.includes(v) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleVersionToggle(v)}
                >
                  {v}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={newActive} onCheckedChange={setNewActive} id="new-active" />
              <Label htmlFor="new-active">Active immediately</Label>
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canCreate || createMutation.isPending || newVersions.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              {createMutation.isPending ? 'Creating...' : `Create Tag${bulkMode ? 's' : ''}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={statusFilter === '' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setStatusFilter(''); setPage(1); }}
        >
          All Tags
        </Button>
        <Button
          variant={statusFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setStatusFilter('active'); setPage(1); }}
          className={statusFilter === 'active' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          <Zap className="h-4 w-4 mr-1" />
          Active (All Builds)
        </Button>
        <Button
          variant={statusFilter === 'staged' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setStatusFilter('staged'); setPage(1); }}
          className={statusFilter === 'staged' ? 'bg-amber-600 hover:bg-amber-700' : ''}
        >
          <FlaskConical className="h-4 w-4 mr-1" />
          Staged (CI Only)
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Version</Label>
              <Select value={odooVersion || 'any'} onValueChange={(v) => { setOdooVersion(v === 'any' ? '' : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Version" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All Versions</SelectItem>
                  {VERSIONS.map((v) => (
                    <SelectItem key={v} value={v}>Odoo {v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Enabled</Label>
              <Select value={active || 'any'} onValueChange={(v) => { setActive(v === 'any' ? '' : v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Enabled" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All</SelectItem>
                  <SelectItem value="true">Enabled</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                placeholder="Search tags..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => { setOdooVersion(''); setActive(''); setStatusFilter(''); setSearch(''); setPage(1); }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load tags.</AlertDescription>
        </Alert>
      )}

      {/* Tags Table */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Test Tags ({pagination?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : tags.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-3 px-2 font-medium">Tag</th>
                      <th className="py-3 px-2 font-medium">Version</th>
                      <th className="py-3 px-2 font-medium">Description</th>
                      <th className="py-3 px-2 font-medium">Stage</th>
                      <th className="py-3 px-2 font-medium">Enabled</th>
                      <th className="py-3 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map((tag: TestTag) => (
                      <tr key={tag.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <code className="bg-muted px-2 py-1 rounded text-xs">{tag.tag}</code>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline">{tag.odoo_version}</Badge>
                        </td>
                        <td className="py-3 px-2 text-muted-foreground max-w-xs truncate">
                          {tag.description || '—'}
                        </td>
                        <td className="py-3 px-2">
                          {tag.status === 'active' ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <Zap className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              <FlaskConical className="h-3 w-3 mr-1" />
                              Staged
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {tag.active ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Enabled</Badge>
                          ) : (
                            <Badge variant="secondary">Disabled</Badge>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            {tag.status === 'staged' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => promoteMutation.mutate(tag.id)}
                                disabled={promoteMutation.isPending}
                                title="Promote to active (apply to all builds)"
                              >
                                <ArrowUpCircle className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => demoteMutation.mutate(tag.id)}
                                disabled={demoteMutation.isPending}
                                title="Demote to staged (CI only)"
                              >
                                <ArrowDownCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(tag)}
                              title="Edit tag"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleMutation.mutate(tag.id)}
                              title="Toggle enabled"
                            >
                              <ToggleLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(tag)}
                              title="Delete tag"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted-foreground">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No tags found.</p>
          )}
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle>Preview Tags by Version</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Label>Version:</Label>
            <Select value={previewVersion} onValueChange={setPreviewVersion}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERSIONS.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Active Tags - All Builds */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Active Tags (All Builds)</span>
                </div>
                {preview?.active_tag_string && (
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(preview.active_tag_string)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {preview?.active_tag_string ? (
                <pre className="bg-green-50 border border-green-200 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                  {preview.active_tag_string}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground italic">No active tags</p>
              )}
            </div>

            {/* Staged Tags - CI Only */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium">Staged Tags (CI Only)</span>
                </div>
                {preview?.staged_tag_string && (
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(preview.staged_tag_string)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {preview?.staged_tag_string ? (
                <pre className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                  {preview.staged_tag_string}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground italic">No staged tags</p>
              )}
            </div>
          </div>

          {/* Combined for CI */}
          {preview?.combined_tag_string && (
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Combined (CI Builds)</span>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(preview.combined_tag_string)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Combined
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                {preview.combined_tag_string}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Test Tag</DialogTitle>
            <DialogDescription>
              Modify the test tag settings
            </DialogDescription>
          </DialogHeader>
          {editingTag && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tag">Tag Name</Label>
                <Input
                  id="edit-tag"
                  value={editingTag.tag}
                  onChange={(e) => setEditingTag({ ...editingTag, tag: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-version">Odoo Version</Label>
                <Select
                  value={editingTag.odoo_version}
                  onValueChange={(v) => setEditingTag({ ...editingTag, odoo_version: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VERSIONS.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={editingTag.description}
                  onChange={(e) => setEditingTag({ ...editingTag, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="edit-active"
                  checked={editingTag.active}
                  onCheckedChange={(checked) => setEditingTag({ ...editingTag, active: checked })}
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => editingTag && updateMutation.mutate(editingTag)}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
